<template>
  <!-- CONTENT -->
  <div
    :id="'screen'"
    :style="{opacity: opacity}"
    v-if="!usersLoading && !transactionsLoading"
  >
    <main
      class="scrollable"
      :style="{paddingTop: '10px'}"
    >
      <!-- Cards -->
      <section :style="{marginBottom: '16px'}">
        <swiper-container
          class="swiper"
          space-between="16"
          slides-per-view="auto"
        >
          <swiper-slide
            v-for="(e, index) in cards"
            :key="e.id"
            class="swiper-slide"
            :style="{
              marginLeft: index === 0 ? '20px' : '0px',
            }"
          >
            <img
              alt="card"
              :src="e.image"
              :class="'clickable'"
              @click="$router.push(e.route)"
              :style="{width: '280px', display: 'block', borderRadius: '14px'}"
            />
          </swiper-slide>
        </swiper-container>
      </section>

      <!-- Operations -->
      <section
        class="container"
        :style="{marginBottom: '30px'}"
      >
        <ul
          class="row-center"
          :style="{gap: '16px'}"
        >
          <li
            v-for="(operation, index) in operations"
            :key="operation.id"
            :style="{
              gap: '7px',
              width: '100%',
              padding: '11px',
              borderRadius: '10px',
              backgroundColor: '#040325',
              marginBottom: index === operations.length - 1 ? '0' : '6px',
            }"
            class="row-center clickable"
            @click="$router.push(operation.route)"
          >
            <component
              :is="operation.icon"
              alt="transaction"
            />
            <div
              :style="{
                display: 'flex',
                flexDirection: 'column',
              }"
            >
              <span
                class="t10 number-of-lines-1"
                :style="{color: '#B4B4C6', lineHeight: '1.2', fontWeight: 600}"
              >
                {{ operation.titleLine1 }}
              </span>
              <span
                class="t10 number-of-lines-1"
                :style="{color: '#B4B4C6', lineHeight: '1.2', fontWeight: 600}"
              >
                {{ operation.titleLine2 }}
              </span>
            </div>
          </li>
        </ul>
      </section>

      <!-- Quick money transfer -->
      <section
        class="container"
        :style="{marginBottom: '30px'}"
      >
        <BlockHeading :title="'Quick money transfer to:'" />
        <swiper-container
          class="swiper"
          space-between="12"
          slides-per-view="auto"
        >
          <swiper-slide
            v-for="e in users"
            :key="e.id"
            class="swiper-slide"
          >
            <div
              :class="'column-center clickable'"
              @click="$router.push(routes.FUND_TRANSFER)"
            >
              <img
                :src="e.photo"
                alt="user"
                :style="{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  marginBottom: '4px',
                }"
              />
              <span :class="'t12 number-of-lines'">{{ e.name }}</span>
            </div>
          </swiper-slide>
          <swiper-slide class="swiper-slide">
            <div :class="'column-center clickable'">
              <div
                alt="user"
                :style="{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  marginBottom: '4px',
                  backgroundColor: '#FFD9C3',
                }"
                :class="'center'"
              >
                <PlusSvg />
              </div>
              <span :class="'t12 number-of-lines'">Choose</span>
            </div>
          </swiper-slide>
        </swiper-container>
      </section>

      <!-- Transactions -->
      <section class="container">
        <BlockHeading
          :rightIcon="SearchSvg"
          :title="'Latest transactions'"
        />
        <ul>
          <li
            v-for="(transaction, index) in transactions"
            :key="transaction.id"
            :style="{
              padding: '10px',
              borderRadius: '10px',
              backgroundColor: '#FFF7F2',
              marginBottom: index === transactions.length - 1 ? '0' : '6px',
            }"
            @click="$router.push(routes.TRANSACTION_DETAILS)"
            class="row-center clickable"
          >
            <img
              alt="transaction"
              :src="transaction.icon"
              :style="{width: '40px', height: '40px', marginRight: '14px'}"
            />
            <div :style="{marginRight: 'auto'}">
              <h6>
                {{ transaction.name }}
              </h6>
              <span class="t12">{{ transaction.type }} </span>
            </div>
            <h6
              :style="{
                color:
                  transaction.direction === 'in'
                    ? '#55ACEE'
                    : 'var(--main-dark)',
              }"
            >
              {{ transaction.direction === 'in' ? '+' : '-' }}
              {{ transaction.amount }}
            </h6>
          </li>
        </ul>
      </section>
    </main>
  </div>

  <!-- LOADER -->
  <Loader v-if="usersLoading || transactionsLoading" />
</template>

<script setup>
// svg
import {PlusSvg} from '@/assets/svg';
import {SearchSvg} from '@/assets/svg';
import {RepeatSvg} from '@/assets/svg';
import {DollarSignSvg} from '@/assets/svg';
import {CreditCardOpSvg} from '@/assets/svg';

// components
import {Loader} from '@/components';
import {BlockHeading} from '@/components';

// other
import 'swiper/css';
import {routes} from '@/routes';
import {useOpacity} from '@/hooks';
import {useGetUsers} from '@/hooks';
import {useGetTransactions} from '@/hooks';
import {Swiper, SwiperSlide} from 'swiper/vue';

const opacity = useOpacity();

let {users, usersLoading} = useGetUsers();
const {transactions, transactionsLoading} = useGetTransactions();

const operations = [
  {
    id: 1,
    titleLine1: 'Receive',
    titleLine2: 'Payment',
    icon: CreditCardOpSvg,
    route: routes.CREATE_INVOICE,
  },
  {
    id: 2,
    titleLine1: 'Money',
    titleLine2: 'Transfer',
    icon: RepeatSvg,
    route: routes.FUND_TRANSFER,
  },
  {
    id: 3,
    titleLine1: 'Make a',
    titleLine2: 'Payment',
    icon: DollarSignSvg,
    route: routes.PAYMENTS,
  },
];

const cards = [
  {
    id: 1,
    image: require('@/assets/cards/03.png'),
    route: routes.CARD_DETAILS,
  },
  {
    id: 2,
    image: require('@/assets/cards/04.png'),
    route: routes.CARD_DETAILS,
  },
];
</script>

<style scoped>
.swiper-slide {
  width: auto !important;
}
</style>
